//@flow

import React from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { BruIcon } from "@/components";
import type { Icon } from "@/components/BruIcon/BruIcon";
import {CreateCanvasFactory, FramePreRenderer, positionStickyFallback} from "@/utils";

type Postfix = {
    icon: Icon,
    text: string,
};

export type BackgroundFrame = {
    image: string,
};

export type TextFrame = {
    prefix: string,
    word: string,
    postfix: Postfix,
};

type BackgroundCanvasProps = { frames: BackgroundFrame[], progress: number };

type BackgroundCanvasState = {
    context?: CanvasRenderingContext2D,
    offscreenContext?: CanvasRenderingContext2D,
};

/**
 * The background part of the DNA animation.
 * Draws the (Lower & Upper) images onto a canvas based on the current progress of the animation.
 */
class BackgroundCanvas extends React.Component<
    BackgroundCanvasProps,
    BackgroundCanvasState,
> {
    +ref: {current: HTMLCanvasElement | null} = React.createRef<HTMLCanvasElement>();

    constructor(props: BackgroundCanvasProps) {
        super(props);
        this.state = {};
    }

    /**
     * Initialize the offscreen canvas for pre-rendering images when component mounts.
     */
    componentDidMount() {
        const canvas = this.ref.current;
        if (canvas == null) {
            return;
        }
        const sources = this.props.frames.map(frame => frame.image);
        const factory = new CreateCanvasFactory(document);
        FramePreRenderer.create(sources, factory).then(
            (renderer: FramePreRenderer) => {
                const offscreenContext = renderer.context;
                canvas.width = offscreenContext.canvas.width;
                canvas.height = offscreenContext.canvas.height / sources.length;
                const context = canvas.getContext("2d", { alpha: false });
                renderer.preload();

                this.setState(state => ({
                    ...state,
                    context,
                    offscreenContext,
                }));
            },
        );
    }

    /**
     * Re-draw the bottom and upper image data based on the current progress.
     */
    componentDidUpdate() {
        const context = this.state.context;
        const offscreenContext = this.state.offscreenContext;
        if (context == null || offscreenContext == null) {
            return;
        }
        const { width, height } = context.canvas;
        const progress = this.props.progress;
        const lowerFrame = Math.floor(progress);
        const upperFrame = lowerFrame + 1;
        const fraction = upperFrame - progress;
        const lowerImageData = offscreenContext.getImageData(
            0,
            height * lowerFrame,
            width,
            height,
        );
        const upperImageData = offscreenContext.getImageData(
            0,
            height * upperFrame,
            width,
            height,
        );
        context.putImageData(lowerImageData, 0, 0);
        context.putImageData(upperImageData, 0, height * fraction);
    }

    render(): React$Element<"canvas"> {
        return <canvas ref={this.ref} />;
    }
}

/**
 * Renders the 3rd column (postfix) part of the DNA animation foreground frame.
 * @param title
 * @param icon
 * @param text
 * @return {JSX.Element}
 * @constructor
 */
const PostfixView = ({ icon, text }: Postfix) => (
    <>
        <div className="spacer content">
            {icon ? <BruIcon icon={icon} /> : <></>}
            {text ? (
                <span className="text-muted py-4 d-block">{text}</span>
            ) : (
                <></>
            )}
        </div>
    </>
);

type ForegroundFrameProps = {
    prefix: string,
    word: string,
    postfix: Postfix,
    progress: number,
};

/**
 * Renders the 3 columns in a DNA animation foreground frame.
 * @param props
 * @return {JSX.Element}
 * @constructor
 */
const ForegroundFrame = (props: ForegroundFrameProps) => {
    const translateY = `translateY(${props.progress * 100}%)`;
    return (
        <div className="frame" style={{ transform: translateY }}>
            <Row className="justify-content-end w-100">
                <Col lg={6} className="align-self-center">
                    <div className="word">
                        <div className="display-1 mb-0 text-uppercase text-white pl-3 pr-5s">
                            {props.word}
                        </div>
                    </div>
                </Col>
                <Col lg={3} className="align-self-end">
                    <div className="postfix">
                        {props.postfix ? (
                            <PostfixView {...props.postfix} />
                        ) : (
                            <></>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export type DnaFrame = TextFrame & BackgroundFrame;

type Props = {
    frames: DnaFrame[],
};

function calculateProgress(
    container: HTMLDivElement,
    wrapper: HTMLDivElement,
): number {
    const wrapperHeight = wrapper.getBoundingClientRect().height;
    /**
     * On mobile devices this animation will be hidden (d-none).
     * Do not update the progress when the wrapper is not visible (height == 0) to prevent re-rendering.
     */
    if (wrapperHeight <= 0) {
        return 0;
    }
    const bounds = container.getBoundingClientRect();
    const max = bounds.height / wrapperHeight;
    const progress = max - bounds.bottom / wrapperHeight;
    return Math.min(max - 1, Math.max(0, progress));
}

type DnaAnimationState = {
    progress: number,
};

/**
 * DNA animation.
 * Renders the background and foreground frames of the animation and updates their progress based on the current scroll position.
 * @return {JSX.Element}
 * @constructor
 */
class DnaAnimation
    extends React.Component<Props, DnaAnimationState> {
    +containerRef: { current: HTMLDivElement | null } = React.createRef<
    HTMLDivElement,
  >();
    +wrapperRef: { current: HTMLDivElement | null } = React.createRef<
    HTMLDivElement,
  >();
  
    constructor() {
        super();
    
        this.state = {
            progress: 0,
        };
    }
  
    onScroll: (() => void) = () => {
        const container = this.containerRef.current;
        const wrapper = this.wrapperRef.current;
        if (container == null || wrapper == null) return;
    
        this.setState(
            prevState => ({
                ...prevState,
                progress: calculateProgress(container, wrapper),
            }),
        );
    };
  
    componentDidMount() {
        document.addEventListener("scroll", this.onScroll.bind(this));
        const wrapper = this.wrapperRef.current;
        if (wrapper == null) return;
        positionStickyFallback(wrapper);
    }
  
    componentWillUnmount() {
        document.removeEventListener("scroll", this.onScroll.bind(this));
    }
  
    shouldComponentUpdate(
        nextProps: Props,
        nextState: DnaAnimationState,
    ): boolean {
        return nextState.progress !== this.state.progress;
    }
  
    render(): React$Element<"div"> {
        const {frames} = this.props;
        const {progress} = this.state;
    
        const fgFrames = frames.map(
            (text: TextFrame, index) => {
                const frameProgress = index - progress;
                return (
                    <ForegroundFrame
                        key={index}
                        prefix={text.prefix}
                        word={text.word}
                        postfix={text.postfix}
                        progress={frameProgress}
                    />
                );
            },
        );
    
        const bgFrames: Array<BackgroundFrame> = frames.map(
            data => ({
                image: data.image,
            }),
        );
    
        const snapAreas = frames.slice(0, frames.length - 1).map(
            (frame, index) => <div key={index} className="snap-area" />,
        );
    
        return (
            <div className="dna-animation-container" ref={this.containerRef}>
                <div className="wrapper snap-area" ref={this.wrapperRef}>
                    <div className="dna-animation">
                        <div className="background">
                            <Row className={"justify-content-center w-100"}>
                                <Col lg={6}>
                                    <BackgroundCanvas frames={bgFrames} progress={progress} />
                                </Col>
                            </Row>
                        </div>
                        <div className="foreground">
                            <div
                                className="prefix display-1 mb-0 text-uppercase text-primary-gradient pr-3"
                            >
                                BE
                            </div>
                            {fgFrames}
                        </div>
                    </div>
                </div>
                {snapAreas}
            </div>
        );
    }
}

export default DnaAnimation;
